import React, { useEffect, useMemo, useState } from 'react';
import { themeColor } from '../../utils/themeColor';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { fmtCurrency, fmtPct, sentimentClass } from './clientReportingFormat';
import { HoverTip, useHoverTip } from './clientReportingHoverTip';
import { brokerAccountDisplayName } from './clientReportingUtils';
import ClientReportingConnectPrompt from './ClientReportingConnectPrompt';
import { EquityDonutSkeleton } from './ClientReportingLoadSkeletons';
import { CLIENT_REPORTING_DEMO_SUMMARY } from './clientReportingDemoPreview';

const EMPTY = {
  gain: { total: null },
  equity: { total: null, investment: null, holdings: null, cash: null },
  profit: { total: null },
};

/**
 * What the ring is made of.
 *
 * Only quantities that add up to the account value belong here. Investment is
 * deliberately excluded: it is capital contributed over time, not a slice of
 * what the account holds today. Drawing it as an arc made the ring total
 * $1,631.95 while the center read $51.42, and handed 97% of the circle to a
 * number that answers a different question.
 */
const RING_SEGMENTS = [
  {
    key: 'holdings',
    label: 'Holdings',
    get color() { return themeColor('#8b5cf6'); },
    description: 'Market value of the positions you currently hold.',
  },
  {
    key: 'cash',
    label: 'Cash',
    color: '#f97316',
    description: 'Uninvested cash sitting in the account.',
  },
];

// Shown under the ring as context, without a slice of its own.
const CONTEXT_ROWS = [
  {
    key: 'investment',
    label: 'Invested',
    description: 'Net capital you put in — deposits minus withdrawals. '
      + 'Account value above or below this is your profit.',
  },
];

const FALLBACK_DESCRIPTIONS = {
  equity: 'Total account value.',
  other: 'Account value your broker has not split into holdings and cash.',
};

// The explanation box is narrow and pinned to the cursor; the geometry lives in
// clientReportingHoverTip so the stock and world-market watchlists behave the
// same way.
const TIP_WIDTH = 220;

function segmentMeta(key) {
  const seg = [...RING_SEGMENTS, ...CONTEXT_ROWS].find((s) => s.key === key);
  if (seg) return seg;
  return { key, label: key === 'other' ? 'Other' : 'Equity', description: FALLBACK_DESCRIPTIONS[key] };
}

function segmentValue(equity, key) {
  const val = equity?.[key];
  return Number.isFinite(Number(val)) && Number(val) > 0 ? Number(val) : 0;
}

function sumFinite(values) {
  const nums = values.filter((v) => Number.isFinite(Number(v)));
  if (!nums.length) return null;
  return nums.reduce((sum, v) => sum + Number(v), 0);
}

function mergeAccountSummaries(clients, summariesById) {
  const pick = (path) => clients.map((c) => {
    const parts = path.split('.');
    let val = summariesById[c.id];
    for (const part of parts) val = val?.[part];
    return val;
  });

  const totalEquity = sumFinite(pick('equity.total'));
  const totalInvestment = sumFinite(pick('equity.investment'));
  const totalHoldings = sumFinite(pick('equity.holdings'));
  const totalCash = sumFinite(pick('equity.cash'));
  const totalProfit = sumFinite(pick('profit.total'));

  let gainTotal = null;
  if (totalProfit != null && totalInvestment != null && totalInvestment !== 0) {
    gainTotal = (totalProfit / Math.abs(totalInvestment)) * 100;
  }

  return {
    gain: { total: gainTotal },
    equity: {
      total: totalEquity,
      investment: totalInvestment,
      holdings: totalHoldings,
      cash: totalCash,
    },
    profit: { total: totalProfit },
  };
}

function buildEquityChartData(equity) {
  const rows = RING_SEGMENTS.map((seg) => ({
    ...seg,
    value: segmentValue(equity, seg.key),
  })).filter((row) => row.value > 0);

  const totalEquity = Number(equity?.total);
  const tracked = rows.reduce((sum, row) => sum + row.value, 0);

  // Brokers that report a total but no holdings/cash split get the difference as
  // "Other", so the ring always adds up to the number in the center.
  if (Number.isFinite(totalEquity) && totalEquity > tracked) {
    const remainder = totalEquity - tracked;
    const onlySlice = rows.length === 0;
    rows.push({
      key: onlySlice ? 'equity' : 'other',
      label: onlySlice ? 'Equity' : 'Other',
      color: onlySlice ? themeColor('#8b5cf6') : 'rgba(148, 163, 184, 0.55)',
      value: remainder,
    });
  }

  if (!rows.length && Number.isFinite(totalEquity) && totalEquity > 0) {
    return [{ key: 'equity', label: 'Equity', color: themeColor('#8b5cf6'), value: totalEquity }];
  }
  return rows;
}

export default function ClientReportingEquityDonut({
  clients = [],
  summariesById = {},
  loading = false,
  showConnectPrompt = false,
  onAddAccount = null,
  connectPromptTitle = 'Add your account',
  connectPromptMessage = 'Connect a brokerage to see your holdings, cash, and invested capital breakdown.',
  connectPromptActionLabel = 'Add / Sync Trades',
}) {
  const [activeAccountId, setActiveAccountId] = useState('all');

  useEffect(() => {
    if (activeAccountId !== 'all' && !clients.some((client) => client.id === activeAccountId)) {
      setActiveAccountId('all');
    }
  }, [clients, activeAccountId]);

  const isDemoPreview = showConnectPrompt && !loading;

  const summary = useMemo(() => {
    if (isDemoPreview) return CLIENT_REPORTING_DEMO_SUMMARY;
    if (activeAccountId === 'all') {
      return mergeAccountSummaries(clients, summariesById);
    }
    const client = clients.find((c) => c.id === activeAccountId);
    if (!client) return EMPTY;
    return summariesById[client.id] || EMPTY;
  }, [activeAccountId, clients, summariesById, isDemoPreview]);

  const { gain, equity, profit } = summary;

  const chartData = useMemo(
    () => buildEquityChartData(equity),
    [equity],
  );

  const legendRows = useMemo(
    () => RING_SEGMENTS.map((seg) => ({
      key: seg.key,
      label: seg.label,
      color: seg.color,
      description: seg.description,
      value: equity?.[seg.key],
    })),
    [equity],
  );

  const contextRows = useMemo(
    () => CONTEXT_ROWS.map((row) => ({
      ...row,
      value: equity?.[row.key],
    })).filter((row) => Number.isFinite(Number(row.value))),
    [equity],
  );

  const hasChart = chartData.some((row) => row.value > 0);

  // Hover/focus target — the ring segments and the legend rows drive the same
  // state, so pointing at either explains the other.
  const {
    activeKey, position: tipPos, showAtCursor, hoverProps, clear: clearActive,
  } = useHoverTip({ width: TIP_WIDTH, height: 84 });
  const activeIndex = chartData.findIndex((row) => row.key === activeKey);

  const chartTotal = useMemo(
    () => chartData.reduce((sum, row) => sum + row.value, 0),
    [chartData],
  );

  const active = useMemo(() => {
    if (!activeKey) return null;
    const meta = segmentMeta(activeKey);
    const slice = chartData.find((row) => row.key === activeKey);
    const known = [...RING_SEGMENTS, ...CONTEXT_ROWS].some((s) => s.key === activeKey);
    const value = known ? equity?.[activeKey] : slice?.value;
    // Share only means something for values actually drawn in the ring, so
    // context rows (Invested) never claim a percentage of it.
    const share = slice && chartTotal > 0 ? (slice.value / chartTotal) * 100 : null;
    return { ...meta, value, share };
  }, [activeKey, chartData, chartTotal, equity]);


  return (
    <section className="vd-panel vd-ml-widget vd-ml-equity-donut">
      <div className="vd-ml-widget-header">
        <h3>Equity</h3>
      </div>

      <div className="vd-ml-donut-account-tabs" role="tablist" aria-label="Equity accounts">
        <button
          type="button"
          role="tab"
          aria-selected={activeAccountId === 'all'}
          className={`vd-ml-donut-account-tab${activeAccountId === 'all' ? ' active' : ''}`}
          onClick={() => setActiveAccountId('all')}
        >
          All
        </button>
        {clients.map((client) => (
          <button
            key={client.id}
            type="button"
            role="tab"
            aria-selected={activeAccountId === client.id}
            className={`vd-ml-donut-account-tab${activeAccountId === client.id ? ' active' : ''}`}
            onClick={() => setActiveAccountId(client.id)}
          >
            {brokerAccountDisplayName(client)}
          </button>
        ))}
      </div>

      <div className={`vd-ml-donut-body${isDemoPreview ? ' is-demo-preview' : ''}`}>
        {isDemoPreview ? (
          <div className="vd-cr-connect-overlay vd-cr-connect-overlay--donut">
            <ClientReportingConnectPrompt
              title={connectPromptTitle}
              message={connectPromptMessage}
              actionLabel={connectPromptActionLabel}
              onAction={onAddAccount}
              compact
            />
          </div>
        ) : null}
        {loading ? (
          <EquityDonutSkeleton />
        ) : (
        <div className="vd-ml-donut-wrap">
          <div className="vd-ml-donut-chart">
            {hasChart ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={chartData}
                    dataKey="value"
                    nameKey="label"
                    innerRadius={68}
                    outerRadius={92}
                    paddingAngle={2}
                    stroke="none"
                    isAnimationActive={false}
                    onMouseEnter={(_, index, event) => showAtCursor(chartData[index]?.key ?? null, event)}
                    onMouseMove={(_, index, event) => showAtCursor(chartData[index]?.key ?? null, event)}
                    onMouseLeave={clearActive}
                  >
                    {chartData.map((entry, index) => (
                      <Cell
                        key={entry.key}
                        fill={entry.color}
                        // The hovered arc keeps full weight; the rest recede so
                        // the reader sees which one they are pointing at.
                        opacity={activeIndex === -1 || activeIndex === index ? 1 : 0.35}
                        style={{ cursor: 'pointer', transition: 'opacity 120ms ease' }}
                      />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="vd-ml-donut-empty" aria-hidden="true" />
            )}

            <div className="vd-ml-donut-center" aria-live="polite">
              {/* Value and label only. The description used to sit here too and
                  overflowed the 68px hole — it now appears as a popover beside
                  the legend row being pointed at, where there is room for it. */}
              {active ? (
                <>
                  <div className="vd-ml-donut-total">{fmtCurrency(active.value)}</div>
                  <div className="vd-ml-donut-active-label">
                    {active.label}
                    {active.share != null ? ` · ${active.share.toFixed(0)}%` : ''}
                  </div>
                </>
              ) : (
                <>
                  <div className="vd-ml-donut-total">{fmtCurrency(equity.total)}</div>
                  <div className={`vd-ml-donut-sub ${sentimentClass(profit.total)}`}>
                    {fmtCurrency(profit.total, { signed: true })}
                  </div>
                  <div className={`vd-ml-donut-sub ${sentimentClass(gain.total)}`}>
                    {fmtPct(gain.total)}
                  </div>
                </>
              )}
            </div>
          </div>

          <ul className="vd-ml-donut-legend">
            {legendRows.map((row) => (
              <li key={row.key}>
                {/* A button so the explanation is reachable by keyboard, not
                    hover only. The values stay rendered either way — the
                    interaction adds meaning, it never gates the numbers. */}
                <button
                  type="button"
                  className={`vd-ml-donut-legend-row${activeKey === row.key ? ' active' : ''}`}
                  {...hoverProps(row.key)}
                  aria-label={`${row.label}: ${fmtCurrency(row.value)}. ${row.description}`}
                >
                  <span className="vd-ml-donut-dot" style={{ background: row.color }} />
                  <span className="vd-ml-donut-legend-label">{row.label}</span>
                  <span className="vd-ml-donut-legend-value">{fmtCurrency(row.value)}</span>
                </button>
              </li>
            ))}

            {/* Below the rule: figures that describe the account but are not
                slices of it, so nobody reads them as part of the ring. */}
            {contextRows.map((row) => (
              <li key={row.key} className="vd-ml-donut-legend-context">
                <button
                  type="button"
                  className={`vd-ml-donut-legend-row${activeKey === row.key ? ' active' : ''}`}
                  {...hoverProps(row.key)}
                  aria-label={`${row.label}: ${fmtCurrency(row.value)}. ${row.description}`}
                >
                  <span className="vd-ml-donut-dot vd-ml-donut-dot-muted" />
                  <span className="vd-ml-donut-legend-label">{row.label}</span>
                  <span className="vd-ml-donut-legend-value">{fmtCurrency(row.value)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
        )}
      </div>

      <HoverTip position={tipPos} width={TIP_WIDTH}>
        {active?.description || null}
      </HoverTip>
    </section>
  );
}
